// src/components/Settings.js
import { useCallback, useEffect, useState } from "react";
import {
  Alert, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, Divider, FormControl, FormControlLabel,
  InputLabel, MenuItem, Paper, Select, Stack, Typography,
} from "@mui/material";
import { supabase } from "../supabaseClient";
import { logger } from "../utils/logger";
import * as groupRequestsService from "../services/groupRequestsService";
import * as usersService from "../services/usersService";
import GoHomeButton from "./GoHomeButton";
import JoinGroupByCode from "./JoinGroupByCode";

export default function Settings({
  userId,
  onSettingsChange,
  activeGroupId,
  setActiveGroupId,
  primaryGroupId,
  onPrimaryGroupChange,
  onNavigateToMessages,
  onNavigateHome,
  onAccountDeleted,
}) {
  const [fontScale, setFontScale] = useState(1.0);
  const [isParent, setIsParent] = useState(false);
  const [initialIsParent, setInitialIsParent] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);

  const [memberships, setMemberships] = useState([]);
  const [switchToGroupId, setSwitchToGroupId] = useState(activeGroupId || "");
  const [settingPrimary, setSettingPrimary] = useState(false);

  const [leaderChangeGroupId, setLeaderChangeGroupId] = useState(null);
  const [leaderChangeMembers, setLeaderChangeMembers] = useState([]);
  const [leaderChangeTargetUserId, setLeaderChangeTargetUserId] = useState("");
  const [submittingLeaderChange, setSubmittingLeaderChange] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [isSoleAdmin, setIsSoleAdmin] = useState(false);
  const [ledGroupsNeedingSuccessor, setLedGroupsNeedingSuccessor] = useState([]);
  const [ledGroupsToAutoDelete, setLedGroupsToAutoDelete] = useState([]);
  const [successorPicks, setSuccessorPicks] = useState({});
  const [loadingLeadership, setLoadingLeadership] = useState(false);

  const loadMemberships = useCallback(async () => {
    const { data } = await supabase
      .from("group_members")
      .select("group_id, groups(id, name, leader_id)")
      .eq("user_id", userId);
    setMemberships(data || []);
  }, [userId]);

  useEffect(() => {
    const load = async () => {
      const s = await usersService.getUserSettings(userId);
      setFontScale(typeof s.font_scale === "number" ? s.font_scale : 1.0);
      setIsParent(!!s.is_parent);
      setInitialIsParent(!!s.is_parent);

      await loadMemberships();

      const profile = await usersService.getUserProfile(userId);
      if (profile?.role === "admin") {
        setIsSoleAdmin(await usersService.checkIsSoleAdmin());
      }
    };
    load();
  }, [userId, loadMemberships]);

  useEffect(() => {
    setSwitchToGroupId(activeGroupId || "");
  }, [activeGroupId]);

  const handleSave = async () => {
    setSaving(true);
    setStatus(null);
    const { data, error } = await usersService.updateUserSettings(userId, {
      font_scale: fontScale,
      is_parent: isParent,
    });
    if (error) {
      setStatus({ type: "error", msg: "Failed to save settings." });
      setSaving(false);
      return;
    }
    const savedIsParent = typeof data?.is_parent === "boolean" ? data.is_parent : isParent;
    onSettingsChange?.({
      font_scale: typeof data?.font_scale === "number" ? data.font_scale : fontScale,
      is_parent: savedIsParent,
    });
    if (savedIsParent !== initialIsParent) {
      logger.info('Settings', 'parent_flag_changed', { isParent: savedIsParent });
      setInitialIsParent(savedIsParent);
    }
    setStatus({ type: "success", msg: "Settings saved." });
    setSaving(false);
  };

  const handleGroupJoined = () => loadMemberships();

  const handleSwitchGroup = () => {
    if (switchToGroupId) {
      setActiveGroupId(switchToGroupId);
      logger.info('Settings', 'active_group_changed', { groupId: switchToGroupId });
    }
  };

  const handleSetPrimary = async (groupId) => {
    setSettingPrimary(true);
    await usersService.setPrimaryGroup({ userId, groupId });
    onPrimaryGroupChange?.(groupId);
    logger.info('Settings', 'primary_group_changed', { groupId });
    setSettingPrimary(false);
  };

  const openLeaderChangeRequest = async (groupId) => {
    setLeaderChangeGroupId(groupId);
    setLeaderChangeTargetUserId("");
    const { data } = await supabase
      .from("group_members")
      .select("user_id, users(display_name)")
      .eq("group_id", groupId);
    setLeaderChangeMembers((data || []).filter((m) => m.user_id !== userId));
  };

  const cancelLeaderChangeRequest = () => {
    setLeaderChangeGroupId(null);
    setLeaderChangeTargetUserId("");
  };

  const submitLeaderChangeRequest = async () => {
    if (!leaderChangeGroupId || !leaderChangeTargetUserId) return;
    setSubmittingLeaderChange(true);
    try {
      await groupRequestsService.submitRequest({
        fromUserId: userId,
        requestType: "leader_change",
        groupId: leaderChangeGroupId,
        targetUserId: leaderChangeTargetUserId,
      });
      cancelLeaderChangeRequest();
    } catch {
      setStatus({ type: "error", msg: "Failed to submit leader change request. Please try again." });
    } finally {
      setSubmittingLeaderChange(false);
    }
  };

  const openDeleteDialog = async () => {
    setDeleteDialogOpen(true);
    setSuccessorPicks({});
    setLoadingLeadership(true);

    const led = memberships.filter((m) => m.groups?.leader_id === userId);
    if (led.length === 0) {
      setLedGroupsNeedingSuccessor([]);
      setLedGroupsToAutoDelete([]);
      setLoadingLeadership(false);
      return;
    }

    const { data } = await supabase
      .from("group_members")
      .select("group_id, user_id, users(display_name)")
      .in("group_id", led.map((m) => m.group_id));

    const needSuccessor = [];
    const autoDelete = [];
    for (const m of led) {
      const others = (data || []).filter((r) => r.group_id === m.group_id && r.user_id !== userId);
      if (others.length === 0) {
        autoDelete.push({ groupId: m.group_id, groupName: m.groups?.name });
      } else {
        needSuccessor.push({ groupId: m.group_id, groupName: m.groups?.name, members: others });
      }
    }
    setLedGroupsNeedingSuccessor(needSuccessor);
    setLedGroupsToAutoDelete(autoDelete);
    setLoadingLeadership(false);
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    const leader_reassignments = ledGroupsNeedingSuccessor.map((g) => ({
      group_id: g.groupId,
      new_leader_id: successorPicks[g.groupId],
    }));
    const { error } = await supabase.functions.invoke("delete-account", {
      method: "POST",
      body: { leader_reassignments },
    });
    if (error) {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setStatus({ type: "error", msg: "Failed to delete account. Please try again." });
      logger.error('Settings', 'account_delete_failed', { error: error.message });
      return;
    }
    ledGroupsNeedingSuccessor.forEach((g) =>
      logger.info('Settings', 'leader_reassigned_before_delete', {
        groupId: g.groupId, oldLeaderId: userId, newLeaderId: successorPicks[g.groupId],
      })
    );
    ledGroupsToAutoDelete.forEach((g) =>
      logger.info('Settings', 'group_deleted_before_delete', { groupId: g.groupId, deletedBecauseEmpty: true })
    );
    logger.info('Settings', 'account_deleted', { userId });
    onAccountDeleted?.();
  };

  return (
    <Paper className="page-card" sx={{ p: 3, mb: 4 }}>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
        <GoHomeButton onNavigateHome={onNavigateHome} />
      </Stack>
      {status && <Alert severity={status.type} sx={{ mb: 2 }}>{status.msg}</Alert>}

      <Stack spacing={2}>
        <FormControl size="small">
          <InputLabel>Font Size</InputLabel>
          <Select label="Font Size" value={fontScale} onChange={(e) => setFontScale(Number(e.target.value))}>
            <MenuItem value={0.9}>Small</MenuItem>
            <MenuItem value={1.0}>Medium</MenuItem>
            <MenuItem value={1.15}>Large</MenuItem>
          </Select>
        </FormControl>

        <FormControlLabel
          control={
            <Checkbox
              checked={isParent}
              onChange={(e) => setIsParent(e.target.checked)}
            />
          }
          label="I'm a parent/guardian/caregiver/etc. - I manage lists for others"
        />

        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? "Saving..." : "Save Settings"}
        </Button>
      </Stack>

      <Divider sx={{ my: 3 }} />

      <Typography variant="h6" sx={{ mb: 2 }}>My Groups</Typography>

      <Stack spacing={2}>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
            MY MEMBERSHIPS
          </Typography>
          <Stack spacing={1}>
            {memberships.map((m) => {
              const isPrimary = m.group_id === primaryGroupId;
              const isLeader = m.groups?.leader_id === userId;
              return (
                <Box key={m.group_id}>
                  <Box
                    sx={{
                      display: "flex", alignItems: "center", gap: 1,
                      bgcolor: "background.paper", border: "1px solid",
                      borderColor: "divider", borderRadius: 2, px: 2, py: 1,
                    }}
                  >
                    <Typography sx={{ fontSize: 16, color: isPrimary ? "warning.main" : "text.disabled" }}>
                      {isPrimary ? "★" : "☆"}
                    </Typography>
                    <Typography variant="body2" sx={{ flex: 1 }}>{m.groups?.name}</Typography>
                    {isPrimary ? (
                      <Chip label="Primary" size="small" color="warning" variant="outlined" />
                    ) : (
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={settingPrimary}
                        onClick={() => handleSetPrimary(m.group_id)}
                      >
                        Set as Primary
                      </Button>
                    )}
                    {isLeader && leaderChangeGroupId !== m.group_id && (
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => openLeaderChangeRequest(m.group_id)}
                      >
                        Request Leader Change
                      </Button>
                    )}
                  </Box>

                  {isLeader && leaderChangeGroupId === m.group_id && (
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1}
                      sx={{ mt: 1, px: 2 }}
                    >
                      <FormControl size="small" fullWidth>
                        <InputLabel>New Leader</InputLabel>
                        <Select
                          label="New Leader"
                          value={leaderChangeTargetUserId}
                          onChange={(e) => setLeaderChangeTargetUserId(e.target.value)}
                        >
                          {leaderChangeMembers.map((member) => (
                            <MenuItem key={member.user_id} value={member.user_id}>
                              {member.users?.display_name || member.user_id}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Button
                        variant="contained"
                        disabled={!leaderChangeTargetUserId || submittingLeaderChange}
                        onClick={submitLeaderChangeRequest}
                      >
                        {submittingLeaderChange ? "Sending..." : "Send Request"}
                      </Button>
                      <Button onClick={cancelLeaderChangeRequest} disabled={submittingLeaderChange}>
                        Cancel
                      </Button>
                    </Stack>
                  )}
                </Box>
              );
            })}
          </Stack>
        </Box>

        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
            CURRENTLY VIEWING
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <FormControl size="small" fullWidth>
              <InputLabel>Active Group</InputLabel>
              <Select
                label="Active Group"
                value={memberships.some(m => m.group_id === switchToGroupId) ? switchToGroupId : ""}
                onChange={(e) => setSwitchToGroupId(e.target.value)}
              >
                {memberships.map((m) => (
                  <MenuItem key={m.group_id} value={m.group_id}>
                    {m.group_id === primaryGroupId ? "★ " : ""}{m.groups?.name}
                    {m.group_id === primaryGroupId ? " (Primary)" : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              onClick={handleSwitchGroup}
              disabled={!switchToGroupId || switchToGroupId === activeGroupId}
              sx={{ minWidth: 100 }}
            >
              Switch
            </Button>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Saves your active group. Navigate to Names to see this group's lists.
          </Typography>
        </Box>

        <JoinGroupByCode
          userGroupIds={memberships.map((m) => m.group_id)}
          onJoined={handleGroupJoined}
        />

        <Button variant="contained" fullWidth onClick={onNavigateToMessages}>
          + Request to leave or create a group
        </Button>
      </Stack>

      <Divider sx={{ my: 3 }} />

      <Typography variant="h6" sx={{ mb: 1, color: "error.main" }}>Danger Zone</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Permanently delete your account and all associated data, including your wishlists,
        claimed items, and group memberships. This cannot be undone.
      </Typography>
      {isSoleAdmin ? (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
          This account cannot be deleted because it is the only administrator.
        </Typography>
      ) : (
        <Button
          variant="outlined"
          color="error"
          onClick={openDeleteDialog}
        >
          Delete My Account
        </Button>
      )}

      <Dialog open={deleteDialogOpen} onClose={() => !deleting && setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Your Account?</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            This will permanently delete your account and all of your data — including your
            wishlists, wishlist items, claimed items, and group memberships. Any items you
            have claimed from others will be unclaimed so they are available again.
            {isParent && (
              <>
                {" "}Any lists you created for the people you manage lists for will be
                permanently deleted too, since they have no account of their own to keep
                them under.
              </>
            )}
            <br /><br />
            <strong>This action cannot be undone.</strong>

            {ledGroupsToAutoDelete.length > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                You are the only member of {ledGroupsToAutoDelete.map((g) => `"${g.groupName}"`).join(", ")}.
                {" "}This group will be permanently deleted along with your account.
              </Alert>
            )}

            {ledGroupsNeedingSuccessor.map((g) => (
              <Box key={g.groupId} sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  You lead <strong>{g.groupName}</strong>. Choose a new Leader before you can delete your account:
                </Typography>
                <FormControl size="small" fullWidth>
                  <InputLabel>New Leader for {g.groupName}</InputLabel>
                  <Select
                    label={`New Leader for ${g.groupName}`}
                    value={successorPicks[g.groupId] || ""}
                    onChange={(e) => setSuccessorPicks((p) => ({ ...p, [g.groupId]: e.target.value }))}
                  >
                    {g.members.map((member) => (
                      <MenuItem key={member.user_id} value={member.user_id}>
                        {member.users?.display_name || member.user_id}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            ))}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteAccount}
            disabled={
              deleting || loadingLeadership ||
              ledGroupsNeedingSuccessor.some((g) => !successorPicks[g.groupId])
            }
          >
            {deleting ? "Deleting…" : "Delete My Account"}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
